/**
 * PhotoModal Component
 *
 * Full-screen photo gallery modal with navigation.
 */

import React, { useEffect, useCallback } from 'react';
import type { ListingPhoto } from '../hooks/useListing';

interface PhotoModalProps {
  photos: ListingPhoto[];
  isOpen: boolean;
  onClose: () => void;
  activeIndex: number;
  onIndexChange: (index: number) => void;
}

export const PhotoModal: React.FC<PhotoModalProps> = ({
  photos,
  isOpen,
  onClose,
  activeIndex,
  onIndexChange,
}) => {
  const goToPrevious = useCallback(() => {
    onIndexChange(activeIndex === 0 ? photos.length - 1 : activeIndex - 1);
  }, [activeIndex, photos.length, onIndexChange]);

  const goToNext = useCallback(() => {
    onIndexChange(activeIndex === photos.length - 1 ? 0 : activeIndex + 1);
  }, [activeIndex, photos.length, onIndexChange]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, goToPrevious, goToNext]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close gallery"
        >
          <CloseIcon className="w-6 h-6 text-white" />
        </button>

        <span className="text-white font-medium">
          {activeIndex + 1} / {photos.length}
        </span>

        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Main Image */}
      <div className="absolute inset-0 flex items-center justify-center p-16">
        <img
          src={photos[activeIndex].url}
          alt={photos[activeIndex].caption || `Photo ${activeIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />
      </div>

      {/* Caption */}
      {photos[activeIndex].caption && (
        <div className="absolute bottom-20 left-0 right-0 text-center">
          <span className="text-white/80 text-sm">
            {photos[activeIndex].caption}
          </span>
        </div>
      )}

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Previous photo"
      >
        <ChevronLeftIcon className="w-8 h-8 text-white" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Next photo"
      >
        <ChevronRightIcon className="w-8 h-8 text-white" />
      </button>

      {/* Thumbnail Strip */}
      <div className="absolute bottom-4 left-0 right-0 overflow-x-auto">
        <div className="flex gap-2 px-4 justify-center">
          {photos.map((photo, idx) => (
            <button
              key={idx}
              onClick={() => onIndexChange(idx)}
              className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-colors ${
                idx === activeIndex
                  ? 'border-white'
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={photo.url}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Icons
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export default PhotoModal;
